import React, { useEffect, useState, useCallback } from "react";
import { getApiBaseUrl, authenticatedRequest } from './Utils.js'
import { useAuth } from "./AuthContext";
import BillingNav from "./BillingNav";

const MONTHS = [
  { key: "1", label: "Jan" },
  { key: "2", label: "Feb" },
  { key: "3", label: "Mar" },
  { key: "4", label: "Apr" },
  { key: "5", label: "May" },
  { key: "6", label: "Jun" },
  { key: "7", label: "Jul" },
  { key: "8", label: "Aug" },
  { key: "9", label: "Sep" },
  { key: "10", label: "Oct" },
  { key: "11", label: "Nov" },
  { key: "12", label: "Dec" }
];

function formatDollar(value) {
  const num = typeof value === "number" ? value : parseFloat(value) || 0;
  if (num < 0) {
    return "($" + Math.abs(num).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ")";
  }
  return "$" + num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function formatReportDate() {
  const now = new Date();
  return now.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

const LegalBillingReport = () => {
  const authUser = useAuth();
  const [apiURL, setApiUrl] = useState(null);
  const [years, setYears] = useState([]);
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [selectedPracticeArea, setSelectedPracticeArea] = useState("");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastProcessed, setLastProcessed] = useState(null);

  useEffect(() => {
    if (authUser && authUser.access_token) {
      getApiBaseUrl(setApiUrl, authUser.access_token);
    }
  }, [authUser]);

  useEffect(() => {
    if (!apiURL || !authUser) return;
    const token = authUser.access_token;

    authenticatedRequest(
      apiURL + "/api/reports/billing/years",
      token,
      (data) => setYears(data || []),
      () => setYears([])
    );

    authenticatedRequest(
      apiURL + "/api/reports/billing/practice-areas",
      token,
      (data) => setPracticeAreas(data || []),
      () => setPracticeAreas([])
    );

    authenticatedRequest(
      apiURL + "/api/reports/billing/last-processed",
      token,
      (data) => setLastProcessed(data.lastProcessedDateTime || null),
      () => setLastProcessed(null)
    );
  }, [apiURL, authUser]);

  const fetchReport = useCallback(() => {
    if (!apiURL || !authUser || !selectedYear || !selectedPracticeArea) return;
    setLoading(true);
    setError(null);
    setReportData(null);

    const url = apiURL + "/api/reports/billing/by-practice-area?year="
      + encodeURIComponent(selectedYear) + "&practiceArea=" + encodeURIComponent(selectedPracticeArea);

    authenticatedRequest(
      url,
      authUser.access_token,
      (data) => {
        setReportData(data);
        setLoading(false);
      },
      () => {
        setError("Failed to load report data.");
        setLoading(false);
      }
    );
  }, [apiURL, authUser, selectedYear, selectedPracticeArea]);

  useEffect(() => {
    if (selectedYear && selectedPracticeArea) {
      fetchReport();
    } else {
      setReportData(null);
    }
  }, [selectedYear, selectedPracticeArea, fetchReport]);

  if (!authUser) {
    sessionStorage.setItem("app:redirect", location.href);
    window.location.replace('/');
    return null;
  }

  function renderTable() {
    if (!reportData || !reportData.categories || reportData.categories.length === 0) {
      return <p className="billing-no-data">No data available for the selected filters.</p>;
    }

    return (
      <div className="billing-table-wrapper">
        <table className="billing-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Matter ID</th>
              <th>Name</th>
              <th></th>
              {MONTHS.map(m => <th key={m.key}>{m.label}</th>)}
              <th>Year</th>
            </tr>
          </thead>
          <tbody>
            {reportData.categories.map((category, catIdx) => {
              const totalMatterRows = category.matters.length * 3;
              return category.matters.map((matter, mIdx) => {
                const monthly = matter.monthly || {};
                const rows = [];

                rows.push(
                  <tr key={matter.matterID + "-budget"} className={"billing-row-budget" + (catIdx % 2 === 1 ? " billing-alt-group" : "")}>
                    {mIdx === 0 && (
                      <td className="billing-cell-category" rowSpan={totalMatterRows}>{category.categoryName}</td>
                    )}
                    <td className="billing-cell-id" rowSpan={3}>{matter.matterID}</td>
                    <td className="billing-cell-name" rowSpan={3}>{matter.name}</td>
                    <td className="billing-cell-label">Budget</td>
                    {MONTHS.map(m => {
                      const val = monthly[m.key] ? monthly[m.key].budget : 0;
                      return <td key={m.key} className="billing-cell-amount">{formatDollar(val)}</td>;
                    })}
                    <td className="billing-cell-amount">{formatDollar(matter.yearBudget)}</td>
                  </tr>
                );

                rows.push(
                  <tr key={matter.matterID + "-spend"} className={"billing-row-spend" + (catIdx % 2 === 1 ? " billing-alt-group" : "")}>
                    <td className="billing-cell-label">Spend</td>
                    {MONTHS.map(m => {
                      const val = monthly[m.key] ? monthly[m.key].spend : 0;
                      return <td key={m.key} className="billing-cell-amount">{formatDollar(val)}</td>;
                    })}
                    <td className="billing-cell-amount">{formatDollar(matter.yearSpend)}</td>
                  </tr>
                );

                rows.push(
                  <tr key={matter.matterID + "-variance"} className={"billing-row-variance" + (catIdx % 2 === 1 ? " billing-alt-group" : "")}>
                    <td className="billing-cell-label billing-label-variance">Variance</td>
                    {MONTHS.map(m => {
                      const b = monthly[m.key] ? monthly[m.key].budget : 0;
                      const s = monthly[m.key] ? monthly[m.key].spend : 0;
                      const variance = b - s;
                      return (
                        <td key={m.key} className={"billing-cell-amount" + (variance < 0 ? " billing-negative" : "")}>
                          {formatDollar(variance)}
                        </td>
                      );
                    })}
                    {(() => {
                      const yearVariance = (matter.yearBudget || 0) - (matter.yearSpend || 0);
                      return (
                        <td className={"billing-cell-amount" + (yearVariance < 0 ? " billing-negative" : "")}>
                          {formatDollar(yearVariance)}
                        </td>
                      );
                    })()}
                  </tr>
                );

                return rows;
              });
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
        <div className="billing-report-page">
          <BillingNav active="by-practice-area" />
        <div className="billing-filters no-print">
            <div className="billing-filter-group">
              <label htmlFor="billing-year">Select Year</label>
              <select
                id="billing-year"
                className="form-control billing-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {years.length === 0 && <option value={selectedYear}>{selectedYear}</option>}
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div className="billing-filter-group">
              <label htmlFor="billing-practice-area">Select a Practice Area</label>
              <select
                id="billing-practice-area"
                className="form-control billing-select"
                value={selectedPracticeArea}
                onChange={(e) => setSelectedPracticeArea(e.target.value)}
              >
                <option value="">-- Select --</option>
                {practiceAreas.map(pa => <option key={pa} value={pa}>{pa}</option>)}
              </select>
            </div>
          </div>

          {selectedPracticeArea && (
            <div className="billing-report-header">
              <div className="billing-header-left">
                <img src="/images/bnsf-logo.png" alt="BNSF Railway" className="billing-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
              <div className="billing-header-center">
                <div className="billing-title">Legal Billing by Practice Area</div>
                <div className="billing-practice-area-name">{selectedPracticeArea}</div>
              </div>
              <div className="billing-header-right">
                <div className="billing-date">{formatReportDate()}</div>
                {lastProcessed && <div className="billing-last-processed">Data as of: {lastProcessed}</div>}
              </div>
            </div>
          )}

          {loading && <div className="billing-loading"><i className="fa fa-spinner fa-spin fa-2x"></i> Loading...</div>}
          {error && <div className="billing-error">{error}</div>}
          {!loading && !error && reportData && renderTable()}
          {!loading && !error && !reportData && selectedPracticeArea && !loading && (
            <p className="billing-no-data">No data available for the selected filters.</p>
          )}
        </div>
    </div>
  );
}

export default LegalBillingReport;
